import { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import LandingPage from '@/pages/LandingPage';
import AuthPage from '@/pages/AuthPage';
import Dashboard from '@/pages/Dashboard';
import EnterCompetition from '@/pages/EnterCompetition';
import AdminDashboard from '@/pages/AdminDashboard';
import SuperAdminDashboard from '@/pages/SuperAdminDashboard';
import RankOverridesPage from '@/pages/RankOverridesPage';
import FeedbackPage from '@/pages/FeedbackPage';
import OrderPage from '@/pages/OrderPage';
import OrderConfirmedPage from '@/pages/OrderConfirmedPage';
import OrderDisplayPage from '@/pages/OrderDisplayPage';
import OrderSettingsPage from '@/pages/OrderSettingsPage';
import StaffOrderPage from '@/pages/StaffOrderPage';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import SettingsPage from '@/pages/SettingsPage';
import RegistrationApp from '@/pages/RegistrationApp';
import RegistrationAdminDashboard from '@/pages/RegistrationAdminDashboard';
import SlugRedirect from '@/pages/SlugRedirect';
import ViewProgrammePage from '@/pages/ViewProgrammePage';
import SuperAdminViewProgrammePage from '@/pages/SuperAdminViewProgrammePage';
import VaultScoring from '@/pages/VaultScoring';
import BarsScoring from '@/pages/BarsScoring';
import BeamScoring from '@/pages/BeamScoring';
import FloorScoring from '@/pages/FloorScoring';
import RandCScoring from '@/pages/RandCScoring';
import RangeScoring from '@/pages/RangeScoring';
import JudgeScoringPageRotation from '@/pages/JudgeScoringPageRotation';
import VCardPage from '@/pages/VCardPage';
import ControlDeskScreen from '@/pages/ControlDeskScreen';
import OpenDashboardPage from '@/pages/OpenDashboardPage';
import MusicControlPage from '@/pages/MusicControlPage';
import FloorMusicPage from '@/pages/FloorMusicPage';
import BackgroundMusicPage from '@/pages/BackgroundMusicPage';
import SpotifyCallbackPage from '@/pages/SpotifyCallbackPage';
import CertificatePrintingPage from '@/pages/CertificatePrintingPage';
import DisplayPage from '@/pages/DisplayPage';
import DisplayPanelPage from '@/pages/DisplayPanelPage';
import SpectatorSupportPage from '@/pages/SpectatorSupportPage';
import LiveScoresPage from '@/pages/LiveScoresPage';
import CoachesLiveScoresPage from '@/pages/CoachesLiveScoresPage';
import RafflePage from '@/pages/RafflePage';
import RaffleAdminPage from '@/pages/RaffleAdminPage';
import PresentationsPage from '@/pages/PresentationsPage';
import PresenterPage from '@/pages/PresenterPage';
import SelfRegistrationPage from '@/pages/SelfRegistrationPage';
import ScoringControlPage from '@/pages/ScoringControlPage';
import SpectatorRegisterPage from '@/pages/SpectatorRegisterPage';
import PrivacyNotice from '@/components/PrivacyNotice';
import Coach from '@/components/Coach';
import MaintenancePage from '@/components/MaintenancePage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Routes that should remain operational during maintenance mode
const OPERATIONAL_ROUTES = [
  '/auth',
  '/superadmin',
  '/admin',
  '/registration',
  '/music',
  '/wagvault',
  '/bars',
  '/beam',
  '/wagfloor',
  '/range',
  '/magfloor',
  '/pommel',
  '/rings',
  '/magvault',
  '/pbars',
  '/highbar',
  '/printing',
  '/livescores',
  '/coaches',
  '/raffle',
  '/open',
  '/display',
  '/control-desk',
  '/presentations',
  '/presenter',
  '/scoringcontrol',
  '/feedback',
  '/order',
  '/order/confirmed',
  '/orderdisplay',
  '/ordersettings',
  '/stafforder',
  '/register'
];
// Wrapper component to conditionally render Coach and PrivacyNotice
function AppExtras({ user }) {
  const location = useLocation();
  
  // Pages that should not show the global Privacy Notice and Coach
  const hiddenPages = ['/', '/vcard', '/spectatorsupport', '/open', '/music', '/music/floor', '/music/background', '/spotify-callback', '/display', '/registration', '/wagvault', '/bars', '/beam', '/wagfloor', '/range', '/magfloor', '/pommel', '/rings', '/magvault', '/pbars', '/highbar', '/printing', '/livescores', '/coaches', '/raffle', '/raffle/admin', '/order', '/scoringcontrol', '/register', '/selfregistration', '/presenter'];
  const shouldHide = hiddenPages.some(page => location.pathname === page || (page !== '/' && location.pathname.startsWith(page)));
  
  if (shouldHide) return null;
  
  return (
    <>
      {user && <Coach isAdmin={user.role === 'admin' || user.role === 'superadmin'} />}
      <PrivacyNotice />
    </>
  );
}

// Axios interceptor for auth
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function App() {
  const [user, setUser] = useState(null);
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceChecked, setMaintenanceChecked] = useState(false);

  useEffect(() => {
    checkMaintenance();
    checkAuth();
  }, []);

  // Quick maintenance check - runs before full app loads
  const checkMaintenance = async () => {
    try {
      const response = await axios.get(`${API}/public/system-status`);
      setMaintenanceMode(response.data.maintenanceMode || false);
    } catch (error) {
      // If check fails, assume not in maintenance
      setMaintenanceMode(false);
    } finally {
      setMaintenanceChecked(true);
    }
  };

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data.user);
      setClub(response.data.club);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setClub(null);
    toast.success('Logged out successfully');
  };

  if (loading || !maintenanceChecked) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-black text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <Toaster position="top-right" />
      <BrowserRouter>
        <MaintenanceCheck 
          maintenanceMode={maintenanceMode} 
          user={user}
          operationalRoutes={OPERATIONAL_ROUTES}
        >
          <Routes>
            {/* Landing Page - Coming Soon */}
            <Route
              path="/"
              element={
                user ? (
                  user.role === 'superadmin' ? (
                    <Navigate to="/scorz-superadmin" replace />
                  ) : user.role === 'admin' ? (
                    <Navigate to="/admin" replace />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )
                ) : (
                  <LandingPage />
                )
              }
            />
          
          {/* Auth page - full login/registration */}
          <Route
            path="/auth"
            element={
              user ? (
                user.role === 'superadmin' ? (
                  <Navigate to="/scorz-superadmin" replace />
                ) : user.role === 'admin' ? (
                  <Navigate to="/admin" replace />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              ) : (
                <AuthPage onAuthSuccess={checkAuth} />
              )
            }
          />
          
          <Route
            path="/dashboard"
            element={
              user && user.role === 'club' ? (
                <Dashboard user={user} club={club} onLogout={logout} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/competition/:competitionId"
            element={
              user && user.role === 'club' ? (
                <EnterCompetition user={user} club={club} onLogout={logout} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/programme/:competitionId"
            element={
              user && user.role === 'club' ? (
                <ViewProgrammePage user={user} club={club} onLogout={logout} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/admin"
            element={
              user && user.role === 'admin' ? (
                <AdminDashboard user={user} onLogout={logout} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/superadmin"
            element={<Navigate to="/superadmin/dashboard" replace />}
          />
          <Route
            path="/scorz-superadmin"
            element={<Navigate to="/superadmin/dashboard" replace />}
          />
          <Route
            path="/registration-admin"
            element={<Navigate to="/registration/admin" replace />}
          />
          <Route
            path="/superadmin/dashboard"
            element={
              user && user.role === 'superadmin' ? (
                <SuperAdminDashboard user={user} onLogout={logout} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/superadmin/view-programme/:competitionId"
            element={
              user && user.role === 'superadmin' ? (
                <SuperAdminViewProgrammePage user={user} onLogout={logout} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/superadmin/rank-overrides"
            element={
              user && user.role === 'superadmin' ? (
                <RankOverridesPage user={user} onLogout={logout} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/settings"
            element={
              user && user.role === 'club' ? (
                <SettingsPage user={user} club={club} onLogout={logout} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/forgot-password"
            element={<ForgotPassword />}
          />
          <Route
            path="/feedback"
            element={<FeedbackPage />}
          />
          {/* Scorz Event Ordering Module */}
          <Route path="/order" element={<OrderPage />} />
          <Route path="/order/confirmed" element={<OrderConfirmedPage />} />
          <Route path="/orderdisplay" element={<OrderDisplayPage />} />
          <Route path="/ordersettings" element={<OrderSettingsPage />} />
          <Route path="/stafforder" element={<StaffOrderPage />} />
          <Route
            path="/reset-password"
            element={<ResetPassword />}
          />
          <Route
            path="/registration"
            element={<RegistrationApp />}
          />
          <Route
            path="/registration/admin"
            element={<RegistrationAdminDashboard />}
          />
          {/* WAG Scoring Routes */}
          <Route
            path="/wagvault"
            element={<VaultScoring />}
          />
          <Route
            path="/bars"
            element={<BarsScoring />}
          />
          <Route
            path="/beam"
            element={<BeamScoring />}
          />
          <Route
            path="/wagfloor"
            element={<FloorScoring />}
          />
          <Route
            path="/range"
            element={<RangeScoring />}
          />
          
          {/* MAG Scoring Routes */}
          <Route
            path="/magfloor"
            element={<JudgeScoringPageRotation apparatus="magfloor" />}
          />
          <Route
            path="/pommel"
            element={<JudgeScoringPageRotation apparatus="pommel" />}
          />
          <Route
            path="/rings"
            element={<JudgeScoringPageRotation apparatus="rings" />}
          />
          <Route
            path="/magvault"
            element={<JudgeScoringPageRotation apparatus="magvault" />}
          />
          <Route
            path="/pbars"
            element={<JudgeScoringPageRotation apparatus="pbars" />}
          />
          <Route
            path="/highbar"
            element={<JudgeScoringPageRotation apparatus="hbar" />}
          />
          <Route
            path="/vcard"
            element={<VCardPage />}
          />
          <Route
            path="/spectatorsupport"
            element={<SpectatorSupportPage />}
          />
          <Route
            path="/selfregistration/terminalone"
            element={<RegistrationApp terminal="terminalone" />}
          />
          <Route
            path="/selfregistration/terminaltwo"
            element={<RegistrationApp terminal="terminaltwo" />}
          />
          <Route
            path="/open"
            element={<OpenDashboardPage />}
          />
          <Route
            path="/music"
            element={<FloorMusicPage />}
          />
          <Route
            path="/music/floor"
            element={<FloorMusicPage />}
          />
          <Route
            path="/music/background"
            element={<BackgroundMusicPage />}
          />
          <Route
            path="/spotify-callback"
            element={<SpotifyCallbackPage />}
          />
          <Route
            path="/display"
            element={<DisplayPage />}
          />
          <Route
            path="/display/a"
            element={<DisplayPanelPage panel="A" />}
          />
          <Route
            path="/display/b"
            element={<DisplayPanelPage panel="B" />}
          />
          <Route
            path="/printing"
            element={<CertificatePrintingPage />}
          />
          <Route
            path="/livescores"
            element={<LiveScoresPage />}
          />
          <Route
            path="/coaches"
            element={<CoachesLiveScoresPage />}
          />
          <Route
            path="/raffle"
            element={<RafflePage />}
          />
          <Route
            path="/raffle/admin"
            element={<RaffleAdminPage />}
          />
          <Route
            path="/presentations"
            element={<PresentationsPage />}
          />
          <Route
            path="/scoringcontrol"
            element={<ScoringControlPage />}
          />
          <Route
            path="/register"
            element={<SpectatorRegisterPage />}
          />

          <Route
            path="/presenter"
            element={<PresenterPage />}
          />

          {/* Custom slug catch-all — keep LAST so it doesn't shadow real routes.
             Resolves scorz.co.uk/<slug> → /?event=<competitionId>. */}
          <Route path="/:slug" element={<SlugRedirect />} />

        </Routes>
        </MaintenanceCheck>
        
        {/* COACH and Privacy Notice - hidden on vcard page */}
        <AppExtras user={user} />
      </BrowserRouter>
    </div>
  );
}

// Component to check if current route should show maintenance page
function MaintenanceCheck({ maintenanceMode, user, operationalRoutes, children }) {
  const location = useLocation();

  // If not in maintenance mode, show normal content
  if (!maintenanceMode) {
    return children;
  }

  // If user is logged in (staff), allow access
  if (user) {
    return children;
  }

  // Per product spec: maintenance only blocks the bare landing page (`/`).
  // Everything else — including custom slug deep-links (e.g. /<slug>) and
  // event deep-links (`/?event=<id>` set by SlugRedirect) — must keep
  // working so clubs can still enter via their direct link.
  const isBareLandingPage =
    location.pathname === '/' && !location.search.includes('event=');

  if (!isBareLandingPage) {
    return children;
  }

  // Also allow explicitly listed operational routes (defensive — covers any
  // routes that may have been added without remembering this flow).
  const isOperationalRoute = operationalRoutes.some(route =>
    location.pathname === route ||
    location.pathname.startsWith(route + '/')
  );

  if (isOperationalRoute) {
    return children;
  }

  // Show maintenance page only for the public landing page
  return <MaintenancePage />;
}

export default App;